import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, BellRing } from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-[#0A0A0C] border border-[#27272A] px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#C5A059] w-full";

function MatchRow({ match, draft, onDraft, onSave, saving }) {
  const d = draft;
  const set = (patch) => onDraft(match.id, patch);
  const setContrib = (i, patch) => {
    const contribs = d.contributions.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    set({ contributions: contribs });
  };
  // Activités (programme 2) : pas d'équipes ni de score
  if (match.phase === "activite") {
    return (
      <div className="border-b border-[#27272A]/60 py-3" data-testid={`match-row-${match.ordre}`}>
        <div className="grid grid-cols-[140px_1fr_auto] items-center gap-2">
          <input value={d.heure} onChange={(e) => set({ heure: e.target.value })} data-testid={`match-heure-${match.ordre}`} className={`${inputCls} text-[#C5A059]`} />
          <input value={d.equipe_a || match.label} disabled className={`${inputCls} italic text-[#71717A]`} title="Animation — contenu fixe" />
          <button onClick={() => onSave(match)} disabled={saving} data-testid={`match-save-${match.ordre}`} title="Enregistrer l'horaire" className="inline-flex items-center gap-1.5 bg-[#C5A059] text-black px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          </button>
        </div>
        <p className="text-[9px] uppercase tracking-wider text-[#71717A] mt-1.5 pl-1">{match.label}</p>
      </div>
    );
  }
  return (
    <div className="border-b border-[#27272A]/60 py-3" data-testid={`match-row-${match.ordre}`}>
      <div className="grid grid-cols-[64px_1fr_52px_16px_52px_1fr_auto] items-center gap-2">
        <input value={d.heure} onChange={(e) => set({ heure: e.target.value })} data-testid={`match-heure-${match.ordre}`} className={`${inputCls} text-[#C5A059]`} />
        <input value={d.equipe_a} onChange={(e) => set({ equipe_a: e.target.value })} data-testid={`match-ea-${match.ordre}`} className={inputCls} />
        <input type="number" min="0" value={d.score_a} onChange={(e) => set({ score_a: e.target.value })} data-testid={`match-sa-${match.ordre}`} className={`${inputCls} text-center`} placeholder="-" />
        <span className="text-[#3f3f46] text-center">—</span>
        <input type="number" min="0" value={d.score_b} onChange={(e) => set({ score_b: e.target.value })} data-testid={`match-sb-${match.ordre}`} className={`${inputCls} text-center`} placeholder="-" />
        <input value={d.equipe_b} onChange={(e) => set({ equipe_b: e.target.value })} data-testid={`match-eb-${match.ordre}`} className={inputCls} />
        <button onClick={() => onSave(match)} disabled={saving} data-testid={`match-save-${match.ordre}`} title="Enregistrer" className="inline-flex items-center gap-1.5 bg-[#C5A059] text-black px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
        </button>
      </div>
      <div className="flex items-center gap-3 mt-1.5 pl-1 flex-wrap">
        <span className="text-[9px] uppercase tracking-wider text-[#71717A]">{match.label}</span>
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#C5A059]/50 text-[#C5A059]">
          {{ masc: "Masculin", fem: "Féminin", etoiles: "Étoiles", act: "Animations" }[match.tournoi] || "Tournoi"}
        </span>
        {match.format && <span className="text-[9px] text-[#71717A]">{match.format}</span>}
        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${match.statut === "joue" ? "border-emerald-400/50 text-emerald-300" : "border-[#27272A] text-[#71717A]"}`} data-testid={`match-statut-${match.ordre}`}>
          {match.statut === "joue" ? "Joué" : "À venir"}
        </span>
        {match.probleme_alignement && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-red-400/60 text-red-300" data-testid={`match-alignement-${match.ordre}`}
            title={`Joueur(s) présent(s) dans les deux équipes : ${(match.joueurs_communs || []).join(", ")}`}>
            Problème d'alignement : {(match.joueurs_communs || []).join(", ")}
          </span>
        )}
      </div>
      {/* Buteurs & passeurs */}
      <div className="mt-2 pl-1 space-y-1.5">
        {d.contributions.map((c, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_64px_64px_28px] items-center gap-2">
            <input value={c.joueur} onChange={(e) => setContrib(i, { joueur: e.target.value })} placeholder="Nom du joueur" data-testid={`match-contrib-nom-${match.ordre}-${i}`} className={inputCls} />
            <select value={c.equipe} onChange={(e) => setContrib(i, { equipe: e.target.value })} data-testid={`match-contrib-eq-${match.ordre}-${i}`} className={inputCls}>
              <option value="a">{d.equipe_a || "Équipe A"}</option>
              <option value="b">{d.equipe_b || "Équipe B"}</option>
            </select>
            <input type="number" min="0" value={c.buts} onChange={(e) => setContrib(i, { buts: e.target.value })} placeholder="Buts" data-testid={`match-contrib-buts-${match.ordre}-${i}`} className={`${inputCls} text-center`} />
            <input type="number" min="0" value={c.passes} onChange={(e) => setContrib(i, { passes: e.target.value })} placeholder="Passes" data-testid={`match-contrib-passes-${match.ordre}-${i}`} className={`${inputCls} text-center`} />
            <button onClick={() => set({ contributions: d.contributions.filter((_, idx) => idx !== i) })} className="text-[#71717A] hover:text-red-400 transition-colors p-1" aria-label="Retirer"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={() => set({ contributions: [...d.contributions, { joueur: "", equipe: "a", buts: 0, passes: 0 }] })} data-testid={`match-contrib-add-${match.ordre}`} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C5A059] hover:text-[#E5C07B] transition-colors pt-1">
          <Plus size={12} /> Ajouter un buteur / passeur
        </button>
      </div>
    </div>
  );
}

export default function AdminMatchs() {
  const [detections, setDetections] = useState([]);
  const [detId, setDetId] = useState("kinshasa-2026");
  const [matchs, setMatchs] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [jourSel, setJourSel] = useState(null);
  const [saving, setSaving] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [testingRappels, setTestingRappels] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/detections`).then((r) => setDetections(r.data)).catch(() => {});
    axios.get(`${API}/matchs`, { params: { detection_id: detId, programme: 2 } })
      .then((r) => {
        setMatchs(r.data);
        const d = {};
        r.data.forEach((m) => {
          d[m.id] = {
            heure: m.heure, equipe_a: m.equipe_a, equipe_b: m.equipe_b,
            categorie: m.categorie || "",
            score_a: m.score_a ?? "", score_b: m.score_b ?? "",
            contributions: (m.contributions || []).map((c) => ({ ...c })),
          };
        });
        setDrafts(d);
      })
      .catch(() => toast.error("Erreur de chargement des matchs"));
  }, [detId]);
  useEffect(load, [load]);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/matchs/seed`, {}, { params: { detection_id: detId }, withCredentials: true });
      toast.success(data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSeeding(false);
    }
  };

  const setDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const save = async (m) => {
    const d = drafts[m.id];
    setSaving(m.id);
    try {
      await axios.patch(`${API}/admin/matchs/${m.id}`, {
        heure: d.heure,
        equipe_a: d.equipe_a,
        equipe_b: d.equipe_b,
        categorie: d.categorie || null,
        score_a: d.score_a === "" ? null : +d.score_a,
        score_b: d.score_b === "" ? null : +d.score_b,
        statut: d.score_a !== "" && d.score_b !== "" ? "joue" : "a_venir",
        contributions: d.contributions
          .filter((c) => c.joueur.trim())
          .map((c) => ({ joueur: c.joueur.trim(), equipe: c.equipe, buts: +c.buts || 0, passes: +c.passes || 0 })),
      }, { withCredentials: true });
      toast.success(`${m.equipe_a} vs ${m.equipe_b} enregistré — visible sur le site`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(null);
    }
  };

  const testerRappels = async () => {
    setTestingRappels(true);
    try {
      const { data } = await axios.post(`${API}/admin/rappels-matchs/test`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch {
      toast.error("Erreur");
    } finally {
      setTestingRappels(false);
    }
  };

  if (matchs === null) return <p className="text-[#71717A] text-sm">Chargement…</p>;

  const parJour = (id) => matchs.filter((m) => m.jour === id);
  // Jours présents dans les données (dates ISO du Programme officiel)
  const jours = [...new Set(matchs.map((m) => m.jour))].sort();
  const jourLabel = (jour) => {
    const label = new Date(`${jour}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  return (
    <div data-testid="admin-matchs" className="max-w-5xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="text-sm text-[#71717A] max-w-2xl">
          Saisissez les <strong className="text-[#C5A059]">scores, buteurs et passeurs</strong> pendant le tournoi — tout s'affiche en direct sur le site (programme + classements). Vous pouvez aussi changer manuellement les équipes d'un match. Les rappels email/push partent automatiquement la veille de chaque match.
        </p>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-[#C5A059] border border-[#C5A059]/50 px-4 py-2.5" data-testid="matchs-prog-officiel">
            Programme officiel — Équipes mixtes & All-Star
          </span>
          <select value={detId} onChange={(e) => setDetId(e.target.value)} data-testid="matchs-filter-detection"
            className="bg-[#0A0A0C] border border-[#27272A] px-4 py-2.5 text-sm focus:outline-none focus:border-[#C5A059]">
            {detections.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
          </select>
          <button onClick={testerRappels} disabled={testingRappels} data-testid="matchs-test-rappels" className="inline-flex items-center gap-2 border border-[#27272A] px-4 py-2.5 text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[#C5A059] hover:border-[#C5A059] transition-colors disabled:opacity-60">
            {testingRappels ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Tester les rappels
          </button>
        </div>
      </div>

      {matchs.length === 0 ? (
        <div className="border border-[#27272A] bg-[#121214] p-10 text-center">
          <p className="text-[#A1A1AA] text-sm mb-6">Aucun match pour cette détection. Initialisez le programme officiel (77 matchs, 12 jours, 4 catégories) pour commencer.</p>
          <button onClick={seed} disabled={seeding} data-testid="matchs-seed-button" className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-8 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {seeding && <Loader2 size={15} className="animate-spin" />} Initialiser le programme officiel
          </button>
        </div>
      ) : (
        <div>
          {/* Jours séparés en onglets en haut */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="matchs-jours-tabs">
            {jours.map((jour) => {
              const jm = parJour(jour);
              const joues = jm.filter((m) => m.statut === "joue").length;
              const actif = (jourSel || jours[0]) === jour;
              return (
                <button
                  key={jour}
                  onClick={() => setJourSel(jour)}
                  data-testid={`matchs-jour-tab-${jour}`}
                  className={`px-4 py-2.5 text-[10px] uppercase tracking-wider border transition-colors ${actif ? "bg-[#C5A059] text-black border-[#C5A059] font-semibold" : "border-[#27272A] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059]"}`}
                >
                  {jourLabel(jour)} · {joues}/{jm.length}
                </button>
              );
            })}
          </div>
          <div className="border border-[#27272A] bg-[#121214] px-6 py-5 overflow-x-auto" data-testid={`matchs-jour-${jourSel || jours[0]}`}>
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[64px_1fr_52px_16px_52px_1fr_auto] gap-2 text-[9px] uppercase tracking-widest text-[#71717A] pb-2 border-b border-[#27272A]">
                <span>Heure</span><span>Équipe A</span><span className="text-center">Score</span><span /><span className="text-center">Score</span><span>Équipe B</span><span />
              </div>
              {parJour(jourSel || jours[0]).map((m) => (
                <MatchRow key={m.id} match={m} draft={drafts[m.id]} onDraft={setDraft} onSave={save} saving={saving === m.id} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
